import { useRef, useState, useCallback } from 'react';

/**
 * Shared camera hook for exercise detection
 *
 * @param {Object} options
 * @param {number} options.maxDimension - Maximum width/height (default: 640, matches training data)
 * @param {string} options.facingMode - Camera facing mode (default: 'user')
 * @returns {Object} Camera state and controls
 */
export function useCamera(options = {}) {
  const {
    maxDimension = 640,
    facingMode = 'user'
  } = options;

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const debugLoggedRef = useRef(false);

  // Start camera
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if mediaDevices API is available (requires HTTPS or localhost)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access requires HTTPS. Please use https:// or localhost.');
      }

      // Step 1: Get camera stream with no dimension constraints to find natural aspect ratio
      let stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false
      });

      // Step 2: Get natural dimensions from the track
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const naturalWidth = settings.width || 640;
      const naturalHeight = settings.height || 480;

      // Step 3: Calculate scale to fit inside maxDimension x maxDimension
      const scale = Math.min(maxDimension / naturalWidth, maxDimension / naturalHeight, 1);
      const targetWidth = Math.round(naturalWidth * scale);
      const targetHeight = Math.round(naturalHeight * scale);

      console.log(`Camera natural: ${naturalWidth}x${naturalHeight}, scaled to: ${targetWidth}x${targetHeight}`);

      // Step 4: If scaling needed, restart with target dimensions
      if (scale < 1) {
        track.stop();
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: targetWidth }, height: { ideal: targetHeight } },
          audio: false
        });
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Wait for video to be ready with timeout for headless browsers
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            // In headless mode, loadedmetadata may not fire, but we have the stream
            console.warn('Video loadedmetadata timeout - proceeding with stream');
            resolve();
          }, 3000);

          videoRef.current.onloadedmetadata = () => {
            clearTimeout(timeout);
            videoRef.current.play().catch(() => {
              // Play might fail in headless mode
            });
            resolve();
          };
        });

        setIsReady(true);
        setIsLoading(false);
        return true;
      }
    } catch (err) {
      console.error('Failed to access camera:', err);
      setError(`Camera error: ${err.message}`);
      setIsLoading(false);
      return false;
    }
  }, [maxDimension, facingMode]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsReady(false);
  }, []);

  // Get canvas 2D context
  const getCanvasContext = useCallback(() => {
    if (!canvasRef.current) return null;
    return canvasRef.current.getContext('2d');
  }, []);

  // Resize canvas to match video (both internal dimensions and CSS display size)
  // On iOS Safari, CSS height: '100%' may not compute correctly relative to
  // the video's auto height. We explicitly set canvas CSS dimensions to match
  // the video element's actual rendered size for proper overlay alignment.
  const syncCanvasSize = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Sync internal canvas dimensions to video pixel dimensions
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      console.log(`[Camera] Canvas internal: ${canvas.width}x${canvas.height}`);
    }

    // Sync CSS display dimensions to video's rendered size
    // This ensures the canvas overlays correctly on iOS Safari where
    // CSS percentage heights may not compute correctly
    const videoRect = video.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();

    // Debug: log size mismatch on first occurrence
    if (!debugLoggedRef.current &&
        (Math.abs(canvasRect.width - videoRect.width) > 1 ||
         Math.abs(canvasRect.height - videoRect.height) > 1)) {
      console.log(`[Camera] Size mismatch - Video: ${videoRect.width.toFixed(0)}x${videoRect.height.toFixed(0)}, Canvas: ${canvasRect.width.toFixed(0)}x${canvasRect.height.toFixed(0)}`);
      debugLoggedRef.current = true;
    }

    // Only update if significantly different (avoid float precision issues)
    if (Math.abs(canvasRect.width - videoRect.width) > 1 ||
        Math.abs(canvasRect.height - videoRect.height) > 1) {
      canvas.style.width = `${videoRect.width}px`;
      canvas.style.height = `${videoRect.height}px`;
    }
  }, []);

  // Clear canvas
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Cleanup
  const cleanup = useCallback(() => {
    stopCamera();
  }, [stopCamera]);

  // Get current stream (for MediaRecorder)
  const getStream = useCallback(() => {
    return streamRef.current;
  }, []);

  return {
    // Refs to attach to elements
    videoRef,
    canvasRef,

    // State
    isReady,
    isLoading,
    error,

    // Controls
    startCamera,
    stopCamera,
    cleanup,

    // Canvas helpers
    getCanvasContext,
    syncCanvasSize,
    clearCanvas,

    // Stream access
    getStream,

    // Max dimension setting
    maxDimension
  };
}
